"use client";

import * as React from "react";

import { cn } from "@oneleet/utils";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { type VariantProps, cva } from "class-variance-authority";

const tooltipContentVariants = cva(
  "animate-popoverIn radix-state-closed:animate-popoverOut text-ng-md z-[100] max-w-md overflow-hidden rounded",
  {
    variants: {
      color: {
        dark: "bg-neutral-900 text-white",
        light:
          "bg-white text-neutral-950 shadow-[0px_0px_0px_1px_rgba(35,34,34,0.08),0px_10px_15px_0px_rgba(17,16,16,0.04),0px_4px_6px_-4px_rgba(17,16,16,0.03)]",
      },
      size: {
        base: "p-2",
        sm: "px-1 py-0.5",
      },
    },
    defaultVariants: {
      size: "base",
    },
  },
);

const tooltipArrowVariants = cva("fill-current", {
  variants: {
    color: {
      dark: "text-neutral-900",
      light: "text-white drop-shadow-[0px_1px_1px_rgba(17,16,16,0.15)]",
    },
  },
});

type NgTooltipProps = React.ComponentProps<typeof TooltipPrimitive.Provider> &
  React.ComponentProps<typeof TooltipPrimitive.Root>;

const NgTooltip = ({ children, ...props }: NgTooltipProps) => (
  <TooltipPrimitive.Provider {...props}>
    <TooltipPrimitive.Root {...props}>{children}</TooltipPrimitive.Root>
  </TooltipPrimitive.Provider>
);
NgTooltip.displayName = TooltipPrimitive.Root.displayName;

/**
 * We keep `asChild={true}` by default for the trigger for the following reasons:
 * While it's true having `asChild` by default makes the tooltip not work with SVGs/icons by default unless you set `asChild={false}` and/or wrap the svg/icon in a span,
 * having `asChild={false}` by default is worse because it will cause tooltips to have issues/not work as expected in many more cases,
 * and makes it very easy for devs to make a mistake that causes the tooltip to behave in an unintended way.
 * Particularly because when `asChild={false}`, the element that's wrapped with `<NgTooltip.Trigger>` gets wrapped in a `<button>`:
 * 1. This does not play well with our buttons like `NgButton` and `NgIconButton` by default because it's not valid html to wrap a button in a button,
 *    so you have to remember to use `asChild` when wrapping a button with a tooltip.
 *    But since there's no real cost to forgetting this (the page still renders fine without any visible errors, only a console error),
 *    many pages with buttons wrapped in tooltips will start to have noisy console errors from devs forgetting this
 * 2. Any element you wrap in a tooltip will appear clickable unless you remember to use `asChild={true}`, even when it's not clickable/should not appear clickable.
 *    This is subtle but confusing for users and often missed/overlooked by devs.
 * 3. SVG icons should likely be wrapped in a `span` when you wrap them in tooltips anyways rather than relying on `asChild={false}`,
 *    as again otherwise the Tooltip's default behavior of wrapping them in a button will make the icon appear clickable even if it isn't/shouldn't appear that way
 */
const NgTooltipTrigger = ({
  asChild,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) => (
  <TooltipPrimitive.Trigger asChild={asChild ?? true} {...props} />
);

const NgTooltipContent = ({
  color = "dark",
  size,
  sideOffset = 4,
  showArrow = true,
  className,
  children,
  ref,
  ...props
}: React.ComponentPropsWithRef<typeof TooltipPrimitive.Content> &
  VariantProps<typeof tooltipContentVariants> & {
    showArrow?: boolean;
  }) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipContentVariants({ color, size }), className)}
      {...props}
    >
      {children}
      {showArrow && (
        <TooltipPrimitive.Arrow className={tooltipArrowVariants({ color })} />
      )}
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
);
NgTooltipContent.displayName = TooltipPrimitive.Content.displayName;

const NgTooltipComponent = Object.assign(NgTooltip, {
  Trigger: NgTooltipTrigger,
  Content: NgTooltipContent,
});

export { NgTooltipComponent as NgTooltip };
